import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import axiosInstance from '../../axiosInstance';

interface Question {
  id: number;
  question_text: string;  // Updated to match API response
  question_tag: string;   // Added to match API response
}

interface ActionArea {
  id: number;
  actionarea_title: string;
}

interface NewCheckpoint {
  name: string;
  date: string;
  type: 'measures' | 'actionareas' | 'questions';
  questionId?: number;
  actionAreaId?: number;
}

interface FormErrors {
  name?: string;
  date?: string;
  type?: string;
  questionId?: string;
  actionAreaId?: string;
}

interface AddCheckpointModalProps {
  show: boolean;
  onHide: () => void;
  onAdd: (newCheckpoint: NewCheckpoint) => void;
  checkpointsCount: number;
  addStatus: string;
  addError: string | null;
}

const AddCheckpointModal: React.FC<AddCheckpointModalProps> = ({ 
  show, 
  onHide, 
  onAdd, 
  checkpointsCount, 
  addStatus, 
  addError 
}) => {
  const dispatch = useDispatch();

  const [newCheckpoint, setNewCheckpoint] = useState<NewCheckpoint>({
    name: '',
    date: '',
    type: 'measures'
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // State for available questions and action areas
  const [questions, setQuestions] = useState<Question[]>([]);
  const [actionAreas, setActionAreas] = useState<ActionArea[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState<boolean>(false);
  const [loadingActionAreas, setLoadingActionAreas] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Get unitId from the Redux store
  const unitId = useSelector((state: any) => state.units.unitId);

  // Initialize the new checkpoint form with default values
  useEffect(() => {
    if (show) {
      initializeNewCheckpoint();
    }
  }, [show, checkpointsCount]);

  // Fetch questions when modal is shown and type is 'questions'
  useEffect(() => {
    if (show && newCheckpoint.type === 'questions') {
      fetchQuestions();
    }
  }, [show, newCheckpoint.type]);

  // Fetch action areas when modal is shown and type is 'actionareas'
  useEffect(() => {
    if (show && newCheckpoint.type === 'actionareas' && unitId) {
      //fetchActionAreas();
    }
  }, [show, newCheckpoint.type, unitId]);

  const initializeNewCheckpoint = () => {
    // Set default date to one week from today
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    const formattedDate = nextWeek.toISOString().split('T')[0];

    setNewCheckpoint({
      name: getDefaultCheckpointName('measures'),
      date: formattedDate,
      type: 'measures'
    });
    setFormErrors({});
  };

  const getDefaultCheckpointName = (type: string, questionId?: number): string => {
    switch(type) {
      case 'measures':
        return "Rückmeldung Maßnahmenumsetzung";
      case 'actionareas':
        return "Ergebnisse dokumentieren";
      case 'questions':
        // Find the selected question
        if (questionId) {
          const question = questions.find(q => q.id === questionId);
          if (question) {
            switch(question.id) {
              case 1:
                return "Kommunikation der Ergebnisse";
              case 2:
                return "Durchführung Auswertungsgespräch";
              case 3:
                return "Dokumentation Handlungsfelder und Maßnahmen";
              default:
                return question.question_tag || "Checkpoint";
            }
          }
        }
        return "Checkpoint";
      default:
        return "Checkpoint";
    }
  };

  const fetchQuestions = async () => {
    setLoadingQuestions(true);
    setFetchError(null);
    
    try {
      const response = await axiosInstance.get('/api/default-questions/');
      setQuestions(response.data);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      setFetchError('Failed to fetch questions. Please try again.');
    } finally {
      setLoadingQuestions(false);
    }
  };

  const fetchActionAreas = async () => {
    if (!unitId) return;
    
    setLoadingActionAreas(true);
    setFetchError(null);
    
    try {
      const response = await axiosInstance.get(`/api/units/${unitId}/actionareas/`);
      setActionAreas(response.data);
    } catch (error) {
      console.error('Failed to fetch action areas:', error);
      setFetchError('Failed to fetch action areas. Please try again.');
    } finally {
      setLoadingActionAreas(false);
    }
  };

  // Handle input changes in the form
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for type changes
    if (name === 'type') {
      // Reset the specific fields when changing type
      const updatedCheckpoint = { 
        ...newCheckpoint, 
        [name]: value,
        questionId: undefined,
        actionAreaId: undefined,
        // Update the name when type changes
        name: getDefaultCheckpointName(value)
      };
      
      setNewCheckpoint(updatedCheckpoint);
      
      // Fetch related data when changing type
      if (value === 'questions') {
        fetchQuestions();
      } else if (value === 'actionareas') {
        //fetchActionAreas();
      }
    } else if (name === 'questionId') {
      // Update the name when a question is selected
      const questionId = value ? Number(value) : undefined;
      setNewCheckpoint(prev => ({ 
        ...prev, 
        [name]: questionId,
        // Only update the name if type is 'questions'
        name: prev.type === 'questions' ? getDefaultCheckpointName('questions', questionId) : prev.name
      }));
    } else {
      setNewCheckpoint(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear validation error when field is changed
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  // Validate the form before submission
  const validateForm = () => {
    const errors: FormErrors = {};
    
    if (!newCheckpoint.name.trim()) {
      errors.name = 'Name ist erforderlich';
    }
    
    if (!newCheckpoint.date) {
      errors.date = 'Datum ist erforderlich';
    }
    
    if (!newCheckpoint.type) {
      errors.type = 'Typ ist erforderlich';
    }

    // Validate type-specific fields
    if (newCheckpoint.type === 'questions' && !newCheckpoint.questionId) {
      errors.questionId = 'Bitte wähle eine Frage aus';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!validateForm()) return;
    onAdd(newCheckpoint);
  };

  // Helper function to get formatted text for action area
  const getActionAreaDisplayText = (actionArea: ActionArea) => {
    if (!actionArea.actionarea_title || actionArea.actionarea_title.trim() === '') {
      return `Handlungsfeld #${actionArea.id} (Ohne Titel)`;
    }
    return actionArea.actionarea_title;
  };

  // Helper function to get formatted text for question
  const getQuestionDisplayText = (question: Question) => {
    // Show tag if available and then the question text
    return question.question_tag;
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title className="fw-bold">Checkpoint hinzufügen</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Typ</Form.Label>
            <Form.Select
              name="type"
              value={newCheckpoint.type}
              onChange={handleFormChange}
              isInvalid={!!formErrors.type}
            >
              <option value="measures">Maßnahmen</option>
              <option value="actionareas">Handlungsfelder</option>
              <option value="questions">Fragen</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {formErrors.type}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Wähle den Typ des Checkpoints aus.
            </Form.Text>
          </Form.Group>

          {/* Conditional fields based on checkpoint type */}
          {newCheckpoint.type === 'questions' && (
            <Form.Group className="mb-3">
              <Form.Label>Frage</Form.Label>
              <Form.Select
                name="questionId"
                value={newCheckpoint.questionId || ''}
                onChange={(e) => handleFormChange({
                  target: {
                    name: 'questionId',
                    value: e.target.value ? Number(e.target.value) : undefined
                  }
                })}
                isInvalid={!!formErrors.questionId}
                disabled={loadingQuestions}
              >
                <option value="">Bitte wähle eine Frage</option>
                {questions.map(question => (
                  <option key={question.id} value={question.id}>
                    {getQuestionDisplayText(question)}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formErrors.questionId}
              </Form.Control.Feedback>
              {loadingQuestions && (
                <div className="d-flex align-items-center mt-2">
                  <Spinner animation="border" size="sm" role="status" className="me-2" />
                  <span>Lade Fragen...</span>
                </div>
              )}
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Datum</Form.Label>
            <Form.Control
              type="date"
              name="date"
              value={newCheckpoint.date}
              onChange={handleFormChange}
              isInvalid={!!formErrors.date}
            />
            <Form.Control.Feedback type="invalid">
              {formErrors.date}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
        
        {fetchError && (
          <Alert variant="warning" className="mt-2">
            {fetchError}
          </Alert>
        )}
        
        {addStatus === 'failed' && (
          <Alert variant="danger" className="mt-2">
            {addError}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Abbrechen
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={addStatus === 'loading' || loadingQuestions || loadingActionAreas}
        >
          {addStatus === 'loading' ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Speichern...
            </>
          ) : (
            'Speichern'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddCheckpointModal;